import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { roundsAPI, playersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../utils/constants';

export default function RoundSetup() {
  const { roundId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const isFriDave = location.pathname.startsWith('/fridave');
  const isClubhouse = location.pathname.startsWith('/clubhouse');
  const backPath = isFriDave ? ROUTES.FRIDAVE_ROUNDS
    : isClubhouse ? ROUTES.CLUBHOUSE_ROUNDS
    : ROUTES.HISTORY;

  const [round, setRound] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');

  // Player assignments: { playerId: groupNumber }
  const [assignments, setAssignments] = useState({});

  useEffect(() => {
    fetchData();
  }, [roundId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const roundData = await roundsAPI.getById(roundId);
      const seriesFilter = roundData.event_type === 'fridave' ? 'fridave'
        : roundData.event_type === 'tml' ? 'tml'
        : roundData.event_type === 'open' ? 'open'
        : null;
      const playersData = await playersAPI.getAll(seriesFilter);

      setRound(roundData);
      setPlayers(playersData.players || []);

      // If round already has players, populate assignments
      if (roundData.players && roundData.players.length > 0) {
        const existingAssignments = {};
        roundData.players.forEach(rp => {
          existingAssignments[rp.player_id] = rp.group_number;
        });
        setAssignments(existingAssignments);
      }
    } catch (err) {
      setError('Failed to load round or players: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPlayer = (playerId, groupNumber) => {
    setAssignments(prev => {
      const newAssignments = { ...prev };
      if (groupNumber === null || groupNumber === '') {
        delete newAssignments[playerId];
      } else {
        newAssignments[playerId] = parseInt(groupNumber);
      }
      return newAssignments;
    });
  };

  const handleAssignPlayers = async () => {
    setError('');

    // Validation
    const assignedPlayers = Object.keys(assignments);
    if (assignedPlayers.length < 2) {
      setError('Please assign at least 2 players to groups');
      return;
    }

    // Check that all assigned players have valid group numbers
    for (const playerId in assignments) {
      if (assignments[playerId] < 1) {
        setError('All group numbers must be at least 1');
        return;
      }
    }

    try {
      setAssigning(true);

      // Build players array for API
      const playersArray = assignedPlayers.map(playerId => ({
        playerId: parseInt(playerId),
        groupNumber: assignments[playerId]
      }));

      // Call API to assign players
      await roundsAPI.assignPlayers(roundId, playersArray);

      // Success - navigate to the round page
      alert(`Successfully assigned ${playersArray.length} players to the round!`);
      navigate(isFriDave ? ROUTES.FRIDAVE_ROUNDS
        : isClubhouse ? ROUTES.CLUBHOUSE_ROUNDS
        : `/rounds/${roundId}`);

    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign players: ' + err.message);
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Round Setup</h1>
        <Card>
          <p className="text-lg text-error">
            You must be logged in as admin to set up rounds.
          </p>
        </Card>
      </div>
    );
  }

  if (!round) {
    return (
      <div className="px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Round Setup</h1>
        <Card>
          <p className="text-lg text-error">Round not found</p>
        </Card>
      </div>
    );
  }

  // Group players by assigned group
  const groupedPlayers = {};
  for (const playerId in assignments) {
    const groupNum = assignments[playerId];
    if (!groupedPlayers[groupNum]) {
      groupedPlayers[groupNum] = [];
    }
    const player = players.find(p => p.id === parseInt(playerId));
    if (player) {
      groupedPlayers[groupNum].push(player);
    }
  }

  const unassignedPlayers = players.filter(p => !assignments[p.id]);
  const assignedCount = Object.keys(assignments).length;
  const groups = Object.keys(groupedPlayers).sort((a, b) => a - b);

  return (
    <div className="px-4 pb-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Round Setup</h1>
      <p className="text-base text-gray-600 mb-6">
        Assign players to groups before starting the round
      </p>

      {error && (
        <div className="bg-red-100 text-red-800 px-6 py-4 border border-red-300 mb-6 rounded-lg">
          {error}
        </div>
      )}

      {/* Round Info */}
      <Card title="Round Details" className="mb-6">
        <div className="space-y-2">
          <div>
            <span className="font-semibold">Event:</span> {round.event_name}
          </div>
          <div>
            <span className="font-semibold">Course:</span> {round.course_name}
          </div>
          <div>
            <span className="font-semibold">Status:</span> {round.status}
          </div>
          <div>
            <span className="font-semibold">Players Assigned:</span> {assignedCount}
          </div>
        </div>
      </Card>

      {/* Unassigned Players */}
      {unassignedPlayers.length > 0 && (
        <Card title="Unassigned Players" className="mb-6">
          <div className="space-y-3">
            {unassignedPlayers.map(player => (
              <div key={player.id} className="flex items-center justify-between p-3 border border-gray-200 rounded">
                <div>
                  <div className="font-semibold">{player.name}</div>
                  <div className="text-sm text-gray-600">Handicap: {player.handicap}</div>
                </div>
                <select
                  value=""
                  onChange={(e) => handleAssignPlayer(player.id, e.target.value)}
                  className="px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Assign to group...</option>
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <option key={n} value={n}>Group {n}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Assigned Groups */}
      {groups.length > 0 && (
        <Card title="Group Assignments" className="mb-6">
          <div className="space-y-4">
            {groups.map(groupNum => (
              <div key={groupNum} className="border border-gray-300 rounded-lg p-4">
                <h3 className="font-bold text-lg mb-3">Group {groupNum}</h3>
                <div className="space-y-2">
                  {groupedPlayers[groupNum].map(player => (
                    <div key={player.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <div className="font-semibold">{player.name}</div>
                        <div className="text-sm text-gray-600">Handicap: {player.handicap}</div>
                      </div>
                      <button
                        onClick={() => handleAssignPlayer(player.id, null)}
                        className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <Button
          variant="secondary"
          onClick={() => navigate(backPath)}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleAssignPlayers}
          disabled={assigning || assignedCount < 2}
        >
          {assigning ? 'Assigning Players...' : 'Assign Players & Continue'}
        </Button>
      </div>
    </div>
  );
}
